import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  acceptRequest,
  cancelMeeting,
  declineRequest,
  keepMeeting,
  fetchRequests,
  fetchAvailability,
  suggestTimes,
  type OwnerRequest,
} from "../api.js";

/**
 * The owner's inbox (1x, 1z).
 *
 * Two things this screen must never imply: that accepting writes to a calendar,
 * and that a movable meeting will be moved for you. Both are stated where the
 * action is, not in a help page.
 */

const TABS = [
  { key: "requested", label: "Requested" },
  { key: "suggested", label: "Suggested" },
  { key: "confirmed", label: "Confirmed" },
  { key: "closed", label: "Closed" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

const CLOSED: OwnerRequest["status"][] = [
  "declined",
  "expired",
  "withdrawn",
  "cancelled",
];

function inTab(request: OwnerRequest, tab: TabKey): boolean {
  if (tab === "closed") return CLOSED.includes(request.status);
  if (tab === "confirmed") {
    return request.status === "confirmed" || request.status === "cancellation_requested";
  }
  return request.status === tab;
}

function when(iso: string, timezone: string): string {
  return new Date(iso).toLocaleString(undefined, {
    timeZone: timezone,
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function countdown(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const hours = Math.round(ms / 3_600_000);
  if (hours < 48) return `expires in ${String(hours)} h`;
  return `expires in ${String(Math.round(hours / 24))} days`;
}

export function Requests({ timezone }: { timezone: string }) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabKey>("requested");
  const [suggestingFor, setSuggestingFor] = useState<string | null>(null);

  const requests = useQuery({ queryKey: ["requests"], queryFn: fetchRequests });
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["requests"] });
    void queryClient.invalidateQueries({ queryKey: ["availability"] });
  };

  const accept = useMutation({ mutationFn: acceptRequest, onSuccess: invalidate });
  const decline = useMutation({ mutationFn: declineRequest, onSuccess: invalidate });
  const cancel = useMutation({ mutationFn: cancelMeeting, onSuccess: invalidate });
  const keep = useMutation({ mutationFn: keepMeeting, onSuccess: invalidate });
  const suggest = useMutation({
    mutationFn: ({ id, slots }: { id: string; slots: string[] }) =>
      suggestTimes(id, slots),
    onSuccess: () => {
      setSuggestingFor(null);
      invalidate();
    },
  });

  const all = requests.data?.requests ?? [];
  const visible = all.filter((request) => inTab(request, tab));

  return (
    <section className="card wide">
      <h1>Requests</h1>
      <p className="lede">
        Times shown in {timezone}. Accepting sends email — it doesn&rsquo;t write to any
        calendar.
      </p>

      <div className="row">
        {TABS.map((entry) => {
          const count = all.filter((request) => inTab(request, entry.key)).length;
          return (
            <button
              key={entry.key}
              type="button"
              className={tab === entry.key ? "chip chip-on" : "chip"}
              aria-pressed={tab === entry.key}
              onClick={() => setTab(entry.key)}
            >
              {entry.label} {count > 0 ? `· ${String(count)}` : ""}
            </button>
          );
        })}
      </div>

      {accept.isError || decline.isError || suggest.isError ? (
        <div className="notice notice-error" role="alert">
          <strong>That didn&rsquo;t go through</strong>
          <span>
            {(accept.error ?? decline.error ?? suggest.error)?.message ??
              "Please try again."}
          </span>
        </div>
      ) : null}

      {requests.isPending ? <p className="lede">Loading…</p> : null}
      {!requests.isPending && visible.length === 0 ? (
        <p className="helper">Nothing here.</p>
      ) : null}

      {visible.map((request) => (
        <article key={request.id} className="request">
          <header className="request-head">
            <div className="account-id">
              <strong>{request.visitor.name ?? request.visitor.email}</strong>
              <span className="mono">{request.visitor.email}</span>
            </div>
            <div className="request-when">
              <strong>{when(request.startUtc, timezone)}</strong>
              <span className="helper">
                {request.durationMinutes} min ·{" "}
                {request.status === "requested"
                  ? countdown(request.expiresAt)
                  : request.status}
              </span>
            </div>
          </header>

          <p className="purpose">{request.purpose}</p>
          {request.flexibilityNote ? (
            <p className="helper">Flexibility: {request.flexibilityNote}</p>
          ) : null}

          {request.status === "requested" && !request.fits ? (
            request.movableClashes.length > 0 ? (
              <div className="clash">
                <strong>This slot only fits if something moves</strong>
                {request.movableClashes.map((clash, index) => (
                  <div key={index} className="reshuffle">
                    <span className="helper">
                      {clash.title ?? "A meeting"} · {clash.organization} ·{" "}
                      {when(clash.startUtc, timezone)} — you marked this movable
                    </span>
                    {clash.couldMoveTo.length > 0 ? (
                      <span className="helper">
                        It would fit at{" "}
                        <strong>
                          {clash.couldMoveTo
                            .map((slot) => when(slot.startUtc, timezone))
                            .join(", or ")}
                        </strong>
                        .
                      </span>
                    ) : (
                      <span className="helper">
                        Nowhere obvious to move it to this week, so this may not be
                        worth it.
                      </span>
                    )}
                  </div>
                ))}
                <span className="helper">
                  Those times are free across every calendar you&rsquo;ve connected.
                  Accepting emails you both; it does not move anything in Outlook or
                  Google — you&rsquo;d reschedule that meeting yourself.
                </span>
              </div>
            ) : (
              <div className="clash clash-hard">
                <strong>You&rsquo;re busy then</strong>
                <span className="helper">
                  Nothing at that time is marked movable. Suggesting other times is
                  probably kinder than declining.
                </span>
              </div>
            )
          ) : null}

          {request.suggestions.length > 0 ? (
            <p className="helper">
              Suggested:{" "}
              {request.suggestions.map((s) => when(s.startUtc, timezone)).join(" · ")}
            </p>
          ) : null}

          {request.status === "cancellation_requested" ? (
            <div className="clash">
              <strong>
                {request.visitor.name ?? request.visitor.email} asked to cancel
              </strong>
              <span className="helper">
                The meeting still stands until you decide. Cancelling emails you both an
                update that removes it from a calendar it was added to.
              </span>
              <div className="row">
                <button
                  type="button"
                  className="primary"
                  onClick={() => cancel.mutate(request.id)}
                  disabled={cancel.isPending}
                >
                  Cancel the meeting
                </button>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => keep.mutate(request.id)}
                  disabled={keep.isPending}
                >
                  Keep it
                </button>
              </div>
            </div>
          ) : null}

          {request.status === "confirmed" ? (
            <div className="row">
              <button
                type="button"
                className="ghost"
                onClick={() => cancel.mutate(request.id)}
                disabled={cancel.isPending}
              >
                Cancel the meeting
              </button>
            </div>
          ) : null}

          {request.status === "requested" ? (
            <>
              <div className="row">
                <button
                  type="button"
                  className="primary"
                  onClick={() => accept.mutate(request.id)}
                  disabled={accept.isPending}
                >
                  {request.fits ? "Accept" : "Accept anyway"}
                </button>
                <button
                  type="button"
                  className="secondary"
                  onClick={() =>
                    setSuggestingFor(suggestingFor === request.id ? null : request.id)
                  }
                >
                  Suggest other times
                </button>
                <button
                  type="button"
                  className="ghost"
                  onClick={() => decline.mutate(request.id)}
                  disabled={decline.isPending}
                >
                  Decline
                </button>
              </div>
              {suggestingFor === request.id ? (
                <SuggestPicker
                  duration={request.durationMinutes}
                  timezone={timezone}
                  pending={suggest.isPending}
                  onSuggest={(slots) => suggest.mutate({ id: request.id, slots })}
                />
              ) : null}
            </>
          ) : null}
        </article>
      ))}
    </section>
  );
}

/** Picks up to three alternatives from times that are genuinely free. */
function SuggestPicker({
  duration,
  timezone,
  pending,
  onSuggest,
}: {
  duration: number;
  timezone: string;
  pending: boolean;
  onSuggest: (slots: string[]) => void;
}) {
  const [chosen, setChosen] = useState<string[]>([]);
  const availability = useQuery({
    queryKey: ["availability", duration],
    queryFn: () => fetchAvailability(duration, 14),
  });

  const slots = (availability.data?.slots ?? []).slice(0, 40);

  return (
    <div className="suggest">
      <p className="helper">
        Pick up to three times you&rsquo;d rather meet. They hold for 48 hours, and the
        first one accepted confirms straight away.
      </p>
      <div className="slotgrid">
        {slots.map((slot) => {
          const on = chosen.includes(slot.startUtc);
          return (
            <button
              key={slot.startUtc}
              type="button"
              className={on ? "slot slot-on" : "slot"}
              aria-pressed={on}
              onClick={() =>
                setChosen((current) =>
                  on
                    ? current.filter((value) => value !== slot.startUtc)
                    : current.length >= 3
                      ? current
                      : [...current, slot.startUtc],
                )
              }
            >
              {when(slot.startUtc, timezone)}
            </button>
          );
        })}
      </div>
      <div className="row">
        <button
          type="button"
          className="primary"
          disabled={chosen.length === 0 || pending}
          onClick={() => onSuggest(chosen)}
        >
          {pending
            ? "Sending…"
            : `Suggest ${String(chosen.length)} time${chosen.length === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  );
}
